import React, { useState, useEffect } from 'react';
import { useLanguage, useAuth, api } from '../context/AppContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Activity, Heart, AlertTriangle, Users, TrendingUp, 
  ChevronLeft, Loader2, Shield, Lock, CheckCircle, XCircle,
  ArrowUp, ArrowDown
} from 'lucide-react';
import { 
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine
} from 'recharts';
import { toast } from 'sonner';

// ==================== PATIENT LIST VIEW ====================
const PatientList = ({ onSelectPatient }) => {
  const { language } = useLanguage();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/admin/clinic/patients');
        setPatients(res.data.patients || []);
      } catch { toast.error('Erreur chargement patients'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-6">
        <Shield className="w-6 h-6 text-red-400" />
        <h2 className="font-heading text-xl uppercase tracking-tight">
          {language === 'fr' ? 'Patients en Mode Clinique' : 'Clinical Mode Patients'}
        </h2>
        <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full">{patients.length}</span>
      </div>

      {patients.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Heart className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>{language === 'fr' ? 'Aucun patient en mode clinique' : 'No patients in clinical mode'}</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {patients.map(p => {
            const cm = p.clinical_mode || {};
            return (
              <button
                key={p.user_id}
                onClick={() => onSelectPatient(p.user_id)}
                className="w-full text-left p-4 bg-card border border-border rounded-xl hover:border-primary/50 transition-all group"
                data-testid={`patient-card-${p.user_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {p.picture ? (
                      <img src={p.picture} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold">
                        {(p.name || '?')[0]}
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-foreground group-hover:text-primary transition-colors">{p.name || 'Inconnu'}</p>
                      <p className="text-xs text-muted-foreground">{p.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {cm.locked_by_coach && <Lock className="w-4 h-4 text-red-400" />}
                    <span className="text-xs text-muted-foreground">{p.meals_this_week || 0} repas/sem</span>
                    <ChevronLeft className="w-4 h-4 text-muted-foreground rotate-180 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
                {(cm.condition_types?.length > 0 || cm.medical_restrictions?.length > 0) && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(cm.condition_types || []).map(c => (
                      <span key={c} className="text-[10px] px-1.5 py-0.5 bg-orange-500/20 text-orange-300 rounded">{c}</span>
                    ))}
                    {(cm.medical_restrictions || []).map(r => (
                      <span key={r} className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-300 rounded">{r.replace(/_/g,' ')}</span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ==================== PATIENT DETAIL VIEW ====================
const PatientAnalytics = ({ userId, onBack }) => {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/admin/analytics/${userId}`);
        setData(res.data);
      } catch { toast.error('Erreur chargement analytics'); }
      finally { setLoading(false); }
    };
    load();
  }, [userId]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  if (!data) return null;

  const { patient, clinical_profile, targets, daily_breakdown, weight_evolution, alerts, compliance_percent, days_tracked, total_meals } = data;

  // Prepare chart data (last 14 days for readability)
  const chartData = daily_breakdown.slice(-14).map(d => ({
    date: d.date.slice(5), // MM-DD
    calories: d.calories,
    protein: d.protein,
    carbs: d.carbs,
    fats: d.fats,
    health: d.health_score,
    meals: d.meals_count
  }));

  const weightData = weight_evolution.map(w => ({
    date: w.date.slice(5),
    weight: w.weight
  }));

  return (
    <div className="space-y-6" data-testid="patient-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="font-heading text-xl uppercase tracking-tight">{patient.name}</h2>
            <p className="text-xs text-muted-foreground">{patient.email}</p>
          </div>
        </div>
        <div className={`text-2xl font-heading font-black ${compliance_percent >= 80 ? 'text-green-400' : compliance_percent >= 50 ? 'text-orange-400' : 'text-red-400'}`}>
          {compliance_percent}%
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPICard icon={<CheckCircle className="w-5 h-5" />} label={language === 'fr' ? 'Conformité' : 'Compliance'} value={`${compliance_percent}%`} color={compliance_percent >= 80 ? 'green' : compliance_percent >= 50 ? 'orange' : 'red'} />
        <KPICard icon={<Activity className="w-5 h-5" />} label={language === 'fr' ? 'Jours suivis' : 'Days tracked'} value={days_tracked} color="blue" />
        <KPICard icon={<TrendingUp className="w-5 h-5" />} label={language === 'fr' ? 'Repas logués' : 'Meals logged'} value={total_meals} color="purple" />
        <KPICard icon={<AlertTriangle className="w-5 h-5" />} label={language === 'fr' ? 'Alertes' : 'Alerts'} value={alerts.length} color={alerts.length > 0 ? 'red' : 'green'} />
      </div>

      {/* Clinical Profile */}
      {clinical_profile.enabled && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-red-400" />
            <span className="text-sm font-medium text-red-300">{language === 'fr' ? 'Profil Clinique' : 'Clinical Profile'}</span>
            {clinical_profile.locked && <Lock className="w-3 h-3 text-red-400" />}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {clinical_profile.restrictions.map(r => (
              <span key={r} className="text-xs px-2 py-0.5 bg-red-500/20 text-red-300 rounded">{r.replace(/_/g, ' ')}</span>
            ))}
          </div>
          <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
            {targets.sodium_limit && <span>Na: {targets.sodium_limit}mg/j</span>}
            {targets.sugar_limit && <span>Sucre: {targets.sugar_limit}g/j</span>}
            {clinical_profile.provider && <span>{clinical_profile.provider}</span>}
          </div>
        </div>
      )}

      {/* ALERTS */}
      {alerts.length > 0 && (
        <div className="space-y-2" data-testid="alerts-section">
          <h3 className="text-sm font-heading uppercase text-red-400 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {language === 'fr' ? 'Alertes Critiques' : 'Critical Alerts'}
          </h3>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {alerts.slice(0, 10).map((a, i) => (
              <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${a.severity === 'critical' ? 'bg-red-500/15 border border-red-500/30 animate-pulse' : 'bg-orange-500/10 border border-orange-500/20'}`}>
                {a.severity === 'critical' ? <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0" />}
                <span className="text-muted-foreground">{a.date}</span>
                <span className="font-medium">
                  {a.type === 'calories_high' && `Calories excessives: ${a.value} / ${a.limit}`}
                  {a.type === 'calories_low' && `Calories insuffisantes: ${a.value} / ${a.limit}`}
                  {a.type === 'protein_low' && `Protéines basses: ${a.value}g / ${a.limit}g`}
                  {a.type === 'sodium_high' && `Sodium élevé: ${a.value}mg / ${a.limit}mg`}
                  {a.type === 'sugar_high' && `Sucre élevé: ${a.value}g / ${a.limit}g`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CALORIES CHART */}
      {chartData.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="text-sm font-heading uppercase mb-3">{language === 'fr' ? 'Calories (14 jours)' : 'Calories (14 days)'}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{fontSize: 10, fill: '#888'}} />
              <YAxis tick={{fontSize: 10, fill: '#888'}} />
              <Tooltip contentStyle={{background: '#1a1a2e', border: '1px solid #333', borderRadius: 8, fontSize: 12}} />
              <ReferenceLine y={targets.calories} stroke="#f59e0b" strokeDasharray="5 5" label={{value: `Cible: ${targets.calories}`, position: 'right', fill: '#f59e0b', fontSize: 10}} />
              <Bar dataKey="calories" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* PROTEIN CHART */}
      {chartData.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="text-sm font-heading uppercase mb-3">{language === 'fr' ? 'Protéines & Macros (14 jours)' : 'Protein & Macros (14 days)'}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{fontSize: 10, fill: '#888'}} />
              <YAxis tick={{fontSize: 10, fill: '#888'}} />
              <Tooltip contentStyle={{background: '#1a1a2e', border: '1px solid #333', borderRadius: 8, fontSize: 12}} />
              <Legend wrapperStyle={{fontSize: 11}} />
              <ReferenceLine y={targets.protein_min} stroke="#ef4444" strokeDasharray="5 5" />
              <Line type="monotone" dataKey="protein" stroke="#22c55e" strokeWidth={2} dot={{r: 3}} name="Protéines (g)" />
              <Line type="monotone" dataKey="carbs" stroke="#3b82f6" strokeWidth={1.5} dot={false} name="Glucides (g)" />
              <Line type="monotone" dataKey="fats" stroke="#f59e0b" strokeWidth={1.5} dot={false} name="Lipides (g)" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* WEIGHT CHART */}
      {weightData.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="text-sm font-heading uppercase mb-3">{language === 'fr' ? 'Poids' : 'Weight'}</h3>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={weightData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{fontSize: 10, fill: '#888'}} />
              <YAxis domain={['dataMin - 5', 'dataMax + 5']} tick={{fontSize: 10, fill: '#888'}} />
              <Tooltip contentStyle={{background: '#1a1a2e', border: '1px solid #333', borderRadius: 8}} />
              <Line type="monotone" dataKey="weight" stroke="#a855f7" strokeWidth={2} dot={{r: 4}} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* DAILY LOG TABLE */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <h3 className="text-sm font-heading uppercase px-4 py-3 border-b border-border">
          {language === 'fr' ? 'Journal Quotidien' : 'Daily Log'}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-secondary/50">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-right">Cal</th>
                <th className="px-3 py-2 text-right">Prot</th>
                <th className="px-3 py-2 text-right">Gluc</th>
                <th className="px-3 py-2 text-right">Lip</th>
                <th className="px-3 py-2 text-center">Repas</th>
                <th className="px-3 py-2 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {daily_breakdown.slice(-14).reverse().map(d => (
                <tr key={d.date} className={`border-t border-border/50 ${d.compliant ? '' : 'bg-red-500/5'}`}>
                  <td className="px-3 py-2 font-mono">{d.date.slice(5)}</td>
                  <td className="px-3 py-2 text-right">{d.calories}</td>
                  <td className="px-3 py-2 text-right text-green-400">{d.protein}g</td>
                  <td className="px-3 py-2 text-right text-blue-400">{d.carbs}g</td>
                  <td className="px-3 py-2 text-right text-orange-400">{d.fats}g</td>
                  <td className="px-3 py-2 text-center">{d.meals_count}</td>
                  <td className="px-3 py-2 text-center">
                    {d.compliant 
                      ? <CheckCircle className="w-4 h-4 text-green-400 mx-auto" />
                      : <XCircle className="w-4 h-4 text-red-400 mx-auto" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ==================== KPI CARD ====================
const KPICard = ({ icon, label, value, color }) => {
  const colors = {
    green: 'text-green-400 bg-green-500/10 border-green-500/20',
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    orange: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20'
  };
  
  return (
    <div className={`rounded-xl border p-3 ${colors[color] || colors.blue}`}>
      <div className="flex items-center gap-1.5 mb-1 opacity-70">{icon}<span className="text-[10px] uppercase">{label}</span></div>
      <p className="text-2xl font-heading font-black">{value}</p>
    </div>
  );
};

// ==================== MAIN PAGE ====================
export default function ClinicalTracking() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [selectedPatient, setSelectedPatient] = useState(null);

  if (!user || (user.role !== 'coach' && user.role !== 'admin')) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <p className="text-muted-foreground">{language === 'fr' ? 'Accès réservé au coach/admin' : 'Coach/Admin access only'}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6" data-testid="clinical-tracking-page">
        {selectedPatient ? (
          <PatientAnalytics userId={selectedPatient} onBack={() => setSelectedPatient(null)} />
        ) : (
          <PatientList onSelectPatient={setSelectedPatient} />
        )}
      </div>
    </Layout>
  );
}
